import { createDraftSafeSelector, OutputSelector } from '@reduxjs/toolkit';
import {
  ApiRequestStatus,
  ProductDetails,
  WalletSections,
} from '../../../../model';
import { getProductCategory } from '../../../../utils/utils';
import { RootState } from '../../../root-store';
import { getSelectedWalletSection } from '../common';
import { selectWalletState, WalletState } from '../wallet-selectors';
import { MyWalletState } from './my-wallet-types';
import { RealEconomyAssetSummary, RealEconomyProductsConcentration } from '../../../../model/wallet/wallet-ow';

export const selectMyWalletState = (state: RootState): MyWalletState =>
  state.features.walletStore.myWallet;

export const selectSharedAccountsState = (state: RootState): MyWalletState =>
  state.features.walletStore.sharedAccounts;

export const getCurrentState: any = createDraftSafeSelector(
  selectWalletState,
  getSelectedWalletSection,
  (state: WalletState, section: WalletSections) => {
    return section === 'MY_WALLET' ? state.myWallet : state.sharedAccounts;
  }
);

export const getWalletMacroCategoriesList: any = createDraftSafeSelector(
  getCurrentState,
  (state: MyWalletState) => {
    return state.wallet?.macroCategories;
  }
);

export const getCategoryForSelectedProduct: any = createDraftSafeSelector(
  getCurrentState,
  (state: MyWalletState) =>
    getProductCategory(state.wallet, state.selectedProduct?.isin)
);

export const getProductDetails: any = createDraftSafeSelector(
  getCurrentState,
  (state: MyWalletState) => state.productDetails
);

export const getTransactions: any = createDraftSafeSelector(
  getProductDetails,
  (productDetails: ProductDetails) => {
    const pageInfo = productDetails.transactionsPaginated.pageInfo;
    return {
      transactions:
        productDetails.transactionsPaginated.items[pageInfo.page] || [],
      pageInfo,
    };
  }
);


export const getUnderlyingGroups: any = createDraftSafeSelector(
  getProductDetails,
  (productDetails: ProductDetails) => {
    const allGroupsRaw = Object.values(productDetails.underlyingGroups.underlyingGroupsPaginated.items).flat();

    const mergedGroupsMap = new Map();
    allGroupsRaw.forEach(group => {
      const existingGroup = mergedGroupsMap.get(group.name);
      if (existingGroup) {
        // Group already exists, merge the 'underlying' arrays
        existingGroup.underlying = existingGroup.underlying.concat(group.underlying);
      } else {
        // First time seeing this group, add a copy to the map
        mergedGroupsMap.set(group.name, { ...group, underlying: [...group.underlying] });
      }
    });
    const finalGroups = Array.from(mergedGroupsMap.values());

    return {
      pageInfo: {
        page: 0,
        count: finalGroups.length,
        perPage: finalGroups.length,
      },
      type: productDetails.underlyingGroups.type,
      equivalent: productDetails.underlyingGroups.equivalent,
      underlyingGroupsList: finalGroups,
    };
  }
);

export const selectRealEconomyAssetSummary = (state: RootState) => {
  return state.features.walletStore.myWallet
    .realEconomyAssetSummary as RealEconomyAssetSummary;
};

export const selectRealEconomyAssetSummaryRequestStatus = (
  state: RootState
) => {
  return state.features.walletStore
    .realEconomyAssetSummaryRequestStatus as ApiRequestStatus;
};

export const selectRealEconomyProductsConcentration = (state: RootState) => {
  return state.features.walletStore.myWallet
    .realEconomyProductsConcentration as RealEconomyProductsConcentration;
};

export const selectRealEconomyProductsConcentrationRequestStatus = (
  state: RootState
) => {
  return state.features.walletStore
    .realEconomyProductsConcentrationRequestStatus as ApiRequestStatus;
};

export const selectHasTotalFia: OutputSelector<
  RootState,
  boolean,
  (res: any[]) => boolean
> = createDraftSafeSelector(
  getWalletMacroCategoriesList,
  (macroCategories: any[]) => {
    const hasTotalFia = macroCategories?.some((cat) => cat.type === 'TotalFia');
    const hasRealEconomy = macroCategories?.some(
      (cat) => cat.type === 'RealEconomy' && (cat.amount?.value > 0 || (cat.commitment?.value || 0) > 0)
    );
    return hasTotalFia && hasRealEconomy;
  }
);

export const selectHasRealEconomy: OutputSelector<
  RootState,
  boolean,
  (res: any[]) => boolean
> = createDraftSafeSelector(
  getWalletMacroCategoriesList,
  (macroCategories: any[]) => {
    return macroCategories?.some((cat) => cat.type === 'RealEconomy');
  }
);

export const selectHasBperProduct: OutputSelector<
  RootState,
  boolean,
  (res: any[]) => boolean
> = createDraftSafeSelector(
  getWalletMacroCategoriesList,
  (macroCategories: any[]) => {
    return macroCategories?.some(
      (cat) =>
        cat.type === 'Liquidity' &&
        cat.categories?.some((c: any) =>
          c.products?.some((p: any) => p.rp === '0812A')
        )
    );
  }
);
